import { createFileRoute } from "@tanstack/react-router";
import { useEditUserProfileMutation, USER_PROFILE_KEY } from "@/modules/user/api/useUser.ts";
import { t } from "@lingui/core/macro";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { PageHeader } from "@/components/PageHeader.tsx";
import { Card, CardTitle } from "@/components/ui/card.tsx";
import { Trans } from "@lingui/react/macro";
import { Separator } from "@/components/ui/separator.tsx";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Info } from "lucide-react";
import { useMeData } from "@/dataProviders/MeDataProvider.tsx";
import { CONFIG } from "@/lib/config.ts";
import { logError } from "@/sentry.ts";
import { emailValidationPattern, phoneValidationPattern } from "@/lib/utils.ts";

export const Route = createFileRoute("/user/user-profile/")({
  component: RouteComponent,
});

const { AUTH_PASSWORD_RESET } = CONFIG;

type Inputs = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  avatarUrl: string;
};

function RouteComponent() {
  const title = t`User profile`;
  const { firstName, lastName, email, phoneNumber } = useMeData();

  const form = useForm<Inputs>({
    defaultValues: {
      firstName,
      lastName,
      email,
      phoneNumber,
    },
  });
  const { isDirty } = form.formState;

  const { mutateAsync } = useEditUserProfileMutation();

  const queryClient = useQueryClient();

  const onSubmit = async ({ firstName, lastName, email, phoneNumber, avatarUrl }: Inputs) => {
    try {
      await mutateAsync({ firstName, lastName, email, phoneNumber, avatarUrl });
      await queryClient.invalidateQueries({ queryKey: [USER_PROFILE_KEY] });
      form.reset({ firstName, lastName, email, phoneNumber });

      toast.success(t`Profile was edited successfully.`);
    } catch (e) {
      logError(e);
      toast.error(t`Something went wrong. We were unable to edit the profile. Please try again.`);
    }
  };

  return (
    <PageWrapper>
      <PageHeader breadcrumbs={[{ href: "/", title }]} />
      <div className="flex flex-col grow p-5">
        <div>
          <Card className={"shadow-none"}>
            <div className={"px-5"}>
              <CardTitle className={"text-xl font-semibold"}>
                <Trans>{title}</Trans>
              </CardTitle>
            </div>

            <Separator />

            <div className={"px-5"}>
              <Form {...form}>
                <form className={"w-full flex flex-col"} onSubmit={form.handleSubmit(onSubmit)}>
                  <div className={"lg:max-w-md"}>
                    <p className={"text-sm font-semibold"}>
                      <Trans>Personal information</Trans>
                    </p>

                    <FormField
                      control={form.control}
                      rules={{ required: t`This field is required` }}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem className={"mt-5"}>
                          <FormLabel>
                            <Trans>First name*</Trans>
                          </FormLabel>
                          <FormControl>
                            <Input placeholder={"Jan"} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      rules={{ required: t`This field is required` }}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem className={"mt-3"}>
                          <FormLabel>
                            <Trans>Last name*</Trans>
                          </FormLabel>
                          <FormControl>
                            <Input placeholder={"Novák"} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      rules={{
                        pattern: { value: emailValidationPattern, message: t`Invalid email` },
                        required: t`This field is required`,
                      }}
                      render={({ field }) => (
                        <FormItem className={"mt-3"}>
                          <FormLabel>
                            <Trans>E-mail*</Trans>
                          </FormLabel>
                          <FormControl>
                            <Input placeholder={"mail@mail.com"} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      rules={{ pattern: { value: phoneValidationPattern, message: t`Invalid phone number` } }}
                      render={({ field }) => (
                        <FormItem className={"mt-3"}>
                          <FormLabel>
                            <Trans>Phone number</Trans>
                          </FormLabel>
                          <FormControl>
                            <Input placeholder={"+420 123 456 789"} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Button type={"submit"} className={"mt-5 ml-auto"} disabled={!isDirty}>
                    <Trans>Save changes</Trans>
                  </Button>
                </form>
              </Form>
            </div>
          </Card>

          <Card className={"shadow-none mt-5"}>
            <div className={"px-5"}>
              <CardTitle className={"text-xl font-semibold"}>
                <Trans>Security</Trans>
              </CardTitle>
            </div>

            <Separator />

            <div className={"px-5"}>
              <div className={"flex flex-col md:flex-row gap-4 items-center"}>
                <Info size={16} />
                <p className={"text-sm lg:basis-1/2"}>
                  <Trans>Click the button to go to a page where you can set a new password.</Trans>
                </p>
              </div>
              <a target={"_blank"} rel={"noopener noreferrer"} href={AUTH_PASSWORD_RESET}>
                <Button type={"button"} variant={"outline"} className={"mt-4"}>
                  <Trans>Change password</Trans>
                </Button>
              </a>
            </div>
          </Card>
        </div>
      </div>
    </PageWrapper>
  );
}
